import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { formatCurrency } from '../../constants/currency';
import type {
    ICampaignCreatorPayoutFeeBreakdown,
    ICampaignHostCreatorMinimumPayoutBreakdown,
} from '../../types/global/campaignFeeBreakdown.types';
import { ROUTES } from '../../types/global/routes.types';
import {
    CREATOR_PAYOUT_SUBSCRIBE_UPSELL_COPY,
    formatCreatorPayoutSubscribedBenefit,
    formatCreatorPayoutUpsellLine,
} from '../../constants/creatorPayoutSubscribeUpsellCopy';
import { resolveCreatorPayoutForViewer } from '../../utils/creatorPayoutBreakdown';

export { CampaignAppInstallPayoutPanel } from './CampaignAppInstallEarningsCalculator';

function formatUsd(value?: number | null): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return formatCurrency(value);
}

function formatRate(rate?: number): string {
    if (rate == null || !Number.isFinite(rate)) return '';
    return `${Math.round(rate * 100)}%`;
}

interface FeeRowProps {
    label: string;
    value: string;
    muted?: boolean;
    highlight?: boolean;
}

function FeeRow({ label, value, muted, highlight }: FeeRowProps) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.35 }}>
            <Typography
                sx={{
                    fontSize: 12,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    color: muted ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.78)',
                }}>
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: 12,
                    fontFamily: highlight ? FONT_ENUM.MAIN_BOLD : FONT_ENUM.MAIN_MEDIUM,
                    color: highlight ? '#ffc965' : '#fff',
                }}>
                {value}
            </Typography>
        </Box>
    );
}

export function CampaignCreatorPayoutFeePanel({
    breakdown,
    subscribeRoute = ROUTES.ACCOUNT_SUBSCRIPTION,
    onViewPlansClick,
    viewerHasActiveSubscription,
}: {
    breakdown: ICampaignCreatorPayoutFeeBreakdown;
    subscribeRoute?: string;
    /** When set, opens in-context subscription flow instead of navigating away. */
    onViewPlansClick?: () => void;
    /** Client-side subscription state; merged with API `viewer_has_active_subscription`. */
    viewerHasActiveSubscription?: boolean;
}) {
    const gross = breakdown.gross_amount_usd;
    const resolved = resolveCreatorPayoutForViewer(breakdown, Boolean(viewerHasActiveSubscription));
    const applied = resolved.applied;

    if (gross == null && !applied) return null;

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: 'rgba(255,255,255,0.5)',
                    mb: 1,
                }}>
                Your payout estimate
            </Typography>
            {gross != null && <FeeRow label="Gross (per creator)" value={formatUsd(gross)} />}
            {applied?.platform_fee_usd != null && (
                <FeeRow
                    label={`Platform fee${
                        applied.platform_fee_rate != null ? ` (${formatRate(applied.platform_fee_rate)})` : ''
                    }`}
                    value={`− ${formatUsd(applied.platform_fee_usd)}`}
                    muted
                />
            )}
            {applied?.net_amount_usd != null && (
                <FeeRow label="You receive" value={formatUsd(applied.net_amount_usd)} highlight />
            )}
            {resolved.showUpsell && breakdown.if_subscribed?.net_amount_usd != null && (
                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', mt: 1, lineHeight: 1.45 }}>
                    {formatCreatorPayoutUpsellLine(
                        formatUsd(resolved.extraVsNonSubscribersUsd),
                        formatUsd(breakdown.if_subscribed.net_amount_usd),
                        formatUsd(breakdown.if_not_subscribed?.net_amount_usd),
                    )}
                </Typography>
            )}
            {resolved.showSubscribedBenefit && (
                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', mt: 1, lineHeight: 1.45 }}>
                    {formatCreatorPayoutSubscribedBenefit(formatUsd(resolved.extraVsNonSubscribersUsd))}
                </Typography>
            )}
            {resolved.showUpsell && (
                <Button
                    {...(onViewPlansClick
                        ? { onClick: onViewPlansClick }
                        : { component: RouterLink, to: subscribeRoute })}
                    size="small"
                    sx={{
                        mt: 1.25,
                        px: 0,
                        minWidth: 0,
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        color: '#ffc965',
                    }}>
                    {CREATOR_PAYOUT_SUBSCRIBE_UPSELL_COPY.viewPlansButtonLabel}
                </Button>
            )}
        </Box>
    );
}

export function CampaignHostCreatorMinimumPayoutPanel({
    breakdown,
    subscribeRoute = ROUTES.BRAND_CAMPAIGNS,
}: {
    breakdown: ICampaignHostCreatorMinimumPayoutBreakdown;
    subscribeRoute?: string;
}) {
    const appliedMin = breakdown.applied?.min_payout_per_creator_usd;
    const subscribedMin = breakdown.if_subscribed?.min_payout_per_creator_usd;
    const unsubscribedMin = breakdown.if_not_subscribed?.min_payout_per_creator_usd;
    const showUpsell =
        breakdown.subscribe_to_save_per_creator_usd != null &&
        breakdown.subscribe_to_save_per_creator_usd > 0 &&
        !breakdown.host_has_active_subscription &&
        subscribedMin != null;

    if (appliedMin == null && !showUpsell) return null;

    return (
        <Box
            sx={{
                mt: 1.5,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
            }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: 'rgba(255,255,255,0.5)',
                    mb: 1,
                }}>
                Minimum per creator{breakdown.tier_name ? ` (${breakdown.tier_name})` : ''}
            </Typography>
            {appliedMin != null && <FeeRow label="Your minimum per creator" value={formatUsd(appliedMin)} highlight />}
            {breakdown.total_platform_fees_usd != null && breakdown.total_platform_fees_usd > 0 && (
                <FeeRow
                    label={`Platform fees (${breakdown.platform_count ?? 0} platform${
                        (breakdown.platform_count ?? 0) === 1 ? '' : 's'
                    })`}
                    value={formatUsd(breakdown.total_platform_fees_usd)}
                    muted
                />
            )}
            {showUpsell && subscribedMin != null && unsubscribedMin != null && (
                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', mt: 1, lineHeight: 1.45 }}>
                    Subscribe to hire {breakdown.tier_name ?? 'creators'} from {formatUsd(subscribedMin)}/creator
                    instead of {formatUsd(unsubscribedMin)} (save{' '}
                    {formatUsd(breakdown.subscribe_to_save_per_creator_usd)}/creator).
                </Typography>
            )}
            {showUpsell && (
                <Button
                    component={RouterLink}
                    to={subscribeRoute}
                    size="small"
                    sx={{
                        mt: 1.25,
                        px: 0,
                        minWidth: 0,
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        color: '#ffc965',
                    }}>
                    View host plans
                </Button>
            )}
        </Box>
    );
}
